import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { featuredVideos } from '@/mocks/videos';
import type { VideoItem } from '@/mocks/videos';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const items = featuredVideos;

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const video: VideoItem = items[current];

  return (
    <section className="relative w-full h-[420px] md:h-[580px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={video.image}
          alt={video.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
      </div>

      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              {video.isHot && (
                <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-primary-500 text-background-50">
                  热播中
                </span>
              )}
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm">
                {video.category}
              </span>
              <span className="text-sm text-background-50/80">{video.year}</span>
            </div>

            <h1 className="text-2xl md:text-5xl font-heading font-bold text-background-50 mb-3 leading-tight">
              {video.title}
            </h1>

            <p className="text-sm md:text-base text-background-50/80 leading-relaxed line-clamp-2 md:line-clamp-3 mb-5 max-w-lg">
              {video.description}
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/article/${video.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <div className="flex items-center gap-3 text-background-50/80 text-sm">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  {video.rating}
                </span>
                <span>{video.duration}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 z-10">
        <div className="w-full px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {items.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  idx === current ? 'w-8 bg-primary-500' : 'w-4 bg-background-50/40'
                }`}
                aria-label={`切换到第${idx + 1}张`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 flex items-center justify-center hover:bg-background-50/30 transition-colors duration-200 cursor-pointer"
              aria-label="上一个"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={next}
              className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 flex items-center justify-center hover:bg-background-50/30 transition-colors duration-200 cursor-pointer"
              aria-label="下一个"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}